import React, { ComponentProps, useCallback, useMemo, useState } from 'react';

import {
  useUsers,
  useRoles,
  useCompanies,
  useDeletedUsers,
  useFormattedUser,
} from '@adsk/alloy-provider';
import {
  DropdownMenu,
  DropdownProps,
  GetFilteredItems,
  OnItemSelect,
  UseAutocompleteProps,
  VirtualProps,
  VirtualizedMenu,
  FooterProps,
  useAutocomplete,
} from '@adsk/alloy-react-dropdown';

import {
  ASSIGNEE_PICKER_COMPANY,
  ASSIGNEE_PICKER_ROLE,
  ASSIGNEE_PICKER_USER,
  DEFAULT_OPTIONS_ORDER,
  DEFAULT_USERS_STATUS_FILTER,
  ASSIGNEE_PICKER_UNASSIGNED,
  AUTODESK_ID_VALUE_KEY,
  MEMBER_GROUP_ID_VALUE_KEY,
  DEFAULT_STATUS_FILTERS,
} from '../consts';
import { AssigneePickerMenuItem } from './AssigneePickerMenuItem';
import { useAssigneePickerAnalytics } from './useAssigneePickerAnalytics';
import { renderAssigneePickerTokens } from '../helpers/avatar';
import {
  transformCompanies,
  transformUsers,
  transformRoles,
} from '../helpers/formatters';
import {
  getAssigneeItemKey,
  isSubtextSearchable,
  sortUsersAlphabetically,
} from '../helpers/utils';
import { useTranslation } from '../i18n';
import {
  AssigneeGroup,
  AssigneePickerRenderItems,
  GroupedItem,
  UseAssigneePickerProps,
  ItemWithGroup,
  AssigneePickerFooterProps,
  AssigneePickerRenderMenuProps,
  OnIsOpenChange,
  VirtualizedMenuRenderParams,
} from '../types';
import { useAssigneeRecents } from './useAssigneeRecents';

type ReturnType = {
  dropdownProps: Pick<
    DropdownProps<ItemWithGroup>,
    | 'isLoading'
    | 'groupedItems'
    | 'renderMenu'
    | 'isAutocomplete'
    | 'showGroupItemCount'
    | 'allowGroupFiltering'
  > & {
    renderItems: AssigneePickerRenderItems;
    getFilteredItems: GetFilteredItems<ItemWithGroup>;
    getItemKey: NonNullable<UseAutocompleteProps<ItemWithGroup>['getItemKey']>;
    itemToKey: (item: ItemWithGroup | null) => string;
    onItemSelect: NonNullable<
      UseAutocompleteProps<ItemWithGroup>['onItemSelect']
    >;
    onIsOpenChange: NonNullable<
      UseAutocompleteProps<ItemWithGroup>['onIsOpenChange']
    >;
    onInputValueChange: NonNullable<
      UseAutocompleteProps<ItemWithGroup>['onInputValueChange']
    >;
  };
};

/** The dropdown's state-change type constants, as passed to onInputValueChange. */
type DropdownStateChangeType =
  (typeof useAutocomplete.stateChangeTypes)[keyof typeof useAutocomplete.stateChangeTypes];

type AssigneePickerVirtualizedMenuProps = {
  menuProps: VirtualizedMenuRenderParams[0];
  virtualProps: VirtualizedMenuRenderParams[1];
};

/**
 * Gives the virtualized menu's hooks a component of their own, so that keying
 * it per open cycle builds a fresh virtualizer. A reused one keeps the scroll
 * offset it held when the menu closed, while the scroll container it reads is
 * remounted back at the top, so the reopened menu paints its rows below the
 * viewport until the next scroll event brings the two back in sync.
 */
// VirtualizedMenu.render is invoked directly, so compiler-added hooks would
// change this component's hook count.
const AssigneePickerVirtualizedMenu = ({
  menuProps,
  virtualProps,
}: AssigneePickerVirtualizedMenuProps) => {
  'use no memo';

  return VirtualizedMenu.render(menuProps, virtualProps);
};

const defaultRenderMenu = ({
  menuProps,
  virtualProps,
  assigneePickerFooterProps,
}: AssigneePickerRenderMenuProps) => (
  <AssigneePickerVirtualizedMenu
    key={menuProps.isOpen ? 'open' : 'closed'}
    menuProps={{
      ...menuProps,
      renderFooter: (footerProps: FooterProps) =>
        menuProps.renderFooter
          ? menuProps.renderFooter({
              ...footerProps,
              ...assigneePickerFooterProps,
            })
          : null,
    }}
    virtualProps={virtualProps}
  />
);

/**
 * A Dropdown component to multiselect items from a dropdown that
 * contains Users, Roles and Companies grouped by category
 */
export const useAssigneePicker = ({
  companyOptionValue = MEMBER_GROUP_ID_VALUE_KEY,
  roleOptionValue = MEMBER_GROUP_ID_VALUE_KEY,
  userOptionValue = AUTODESK_ID_VALUE_KEY,
  companyOptionLabel,
  roleOptionLabel,
  userOptionLabel,
  userOptionSubtextRenderer,
  optionsOrder = DEFAULT_OPTIONS_ORDER,
  statusFilters = DEFAULT_STATUS_FILTERS,
  optionsFilter = {},
  additionalCompanyDataFields,
  additionalUserDataFields,
  additionalRoleDataFields,
  customUsers,
  customRoles,
  customCompanies,
  customUnassigned,
  postFormatters,
  hideDeletedEntitiesInMenu = { users: true },
  hideInactiveEntitiesInMenu = true,
  renderFooter,
  renderMenuItem = AssigneePickerMenuItem,
  renderMenu = defaultRenderMenu,
  renderItems = renderAssigneePickerTokens,
  disabledRecents = true,
  fieldId,
  onItemSelect: onItemSelectProp,
  onIsOpenChange: onIsOpenChangeProp,
  onInputValueChange: onInputValueChangeProp,
}: UseAssigneePickerProps | undefined = {}) => {
  const { t } = useTranslation();

  // selectedGroup is owned here so it can be computed before passing to the
  // analytics hook — breaking the circular dependency with groupedItems.
  const [selectedGroup, setSelectedGroup] = useState<string>('all');

  const UNASSIGNED_ITEM = useMemo(
    () =>
      customUnassigned ||
      ({
        label: t('UNASSIGNED'),
        items: [
          {
            label: t('UNASSIGNED'),
            value: 'null',
            group: 'unassigned',
          },
        ],
        group: ASSIGNEE_PICKER_UNASSIGNED,
      } satisfies GroupedItem),
    [customUnassigned, t],
  );

  const alloyUsers = useUsers();
  const users = useMemo(
    () => customUsers || alloyUsers,
    [customUsers, alloyUsers],
  );

  const [searchText, setSearchText] = useState<string>('');

  const { users: statusFiltersUsers = DEFAULT_USERS_STATUS_FILTER } =
    statusFilters;

  const includeDeletedUsers = useMemo(
    () => statusFiltersUsers.includes('deleted') && !customUsers,
    [customUsers, statusFiltersUsers],
  );
  const alloyDeletedUsers = useDeletedUsers({ disable: !includeDeletedUsers });

  const alloyRoles = useRoles();
  const roles = useMemo(
    () => customRoles || alloyRoles,
    [customRoles, alloyRoles],
  );

  const alloyCompanies = useCompanies();
  const companies = useMemo(
    () => customCompanies || alloyCompanies,
    [customCompanies, alloyCompanies],
  );

  const isLoading = useMemo(
    () =>
      (includeDeletedUsers
        ? [users, roles, companies, alloyDeletedUsers]
        : [users, roles, companies]
      ).some((entity) => entity?.loading),
    [includeDeletedUsers, users, roles, companies, alloyDeletedUsers],
  );

  const { formatDisplayUser } = useFormattedUser();
  const companyOptionsFilter = useMemo(
    () => optionsFilter[ASSIGNEE_PICKER_COMPANY],
    [optionsFilter],
  );
  const roleOptionsFilter = useMemo(
    () => optionsFilter[ASSIGNEE_PICKER_ROLE],
    [optionsFilter],
  );
  const userOptionsFilter = useMemo(
    () => optionsFilter[ASSIGNEE_PICKER_USER],
    [optionsFilter],
  );

  const baseGroupedItems = useMemo(() => {
    const [companyIdToNameMap, companyOptions] = transformCompanies(
      companies.data,
      companyOptionValue,
      t,
      statusFilters.companies,
      companyOptionsFilter,
      additionalCompanyDataFields,
      postFormatters?.postTransformCompanies,
    );

    const [rolesMemberCount, userOptions] = transformUsers(
      includeDeletedUsers
        ? sortUsersAlphabetically([...users.data, ...alloyDeletedUsers.data])
        : users.data,
      userOptionValue,
      formatDisplayUser,
      t,
      statusFiltersUsers,
      userOptionsFilter,
      additionalUserDataFields,
      companyIdToNameMap,
      roles.data,
      userOptionSubtextRenderer,
      postFormatters?.postTransformUsers,
      includeDeletedUsers && hideDeletedEntitiesInMenu.users,
    );

    const roleItems = transformRoles(
      roles.data,
      roleOptionValue,
      t,
      statusFilters.roles,
      roleOptionsFilter,
      additionalRoleDataFields,
      rolesMemberCount,
      postFormatters?.postTransformRoles,
      hideInactiveEntitiesInMenu,
    );

    const allOptions: { [key in AssigneeGroup]: GroupedItem[] } = {
      unassigned: [UNASSIGNED_ITEM],
      company: [
        {
          label: companyOptionLabel || t('COMPANIES'),
          items: companyOptions,
          group: ASSIGNEE_PICKER_COMPANY,
        },
      ],
      role: [
        {
          label: roleOptionLabel || t('ROLES'),
          items: roleItems,
          group: ASSIGNEE_PICKER_ROLE,
        },
      ],
      user: [
        {
          label: userOptionLabel || t('MEMBERS'),
          items: userOptions,
          group: ASSIGNEE_PICKER_USER,
        },
      ],
    };

    return isLoading
      ? []
      : optionsOrder.reduce<GroupedItem[]>(
          (acc, category) => [...acc, ...allOptions[category]],
          [],
        );
  }, [
    companies.data,
    companyOptionValue,
    t,
    statusFilters.companies,
    statusFilters.roles,
    companyOptionsFilter,
    additionalCompanyDataFields,
    postFormatters?.postTransformCompanies,
    postFormatters?.postTransformUsers,
    postFormatters?.postTransformRoles,
    includeDeletedUsers,
    users.data,
    alloyDeletedUsers.data,
    userOptionValue,
    formatDisplayUser,
    statusFiltersUsers,
    userOptionsFilter,
    additionalUserDataFields,
    userOptionSubtextRenderer,
    hideDeletedEntitiesInMenu.users,
    roles.data,
    roleOptionValue,
    roleOptionsFilter,
    additionalRoleDataFields,
    hideInactiveEntitiesInMenu,
    UNASSIGNED_ITEM,
    companyOptionLabel,
    roleOptionLabel,
    userOptionLabel,
    isLoading,
    optionsOrder,
  ]);

  const { groupedItems, onItemSelect, onIsOpenChange } = useAssigneeRecents({
    disabledRecents,
    fieldId,
    baseGroupedItems,
    selectedGroup,
    isLoading,
    onItemSelect: onItemSelectProp,
    onIsOpenChange: onIsOpenChangeProp,
    isSearchActive: !!searchText.trim(),
  });

  const {
    searchTextRef,
    getSetSelectedGroupKey,
    renderMenuHandler,
    trackItemSelect,
    onMenuOpenChange,
  } = useAssigneePickerAnalytics({
    groupedItems,
    selectedGroup,
  });

  const onItemSelectWithAnalytics: OnItemSelect<ItemWithGroup> = useCallback(
    (item, context) => {
      trackItemSelect(item, context);
      onItemSelect(item, context);
    },
    [trackItemSelect, onItemSelect],
  );

  const onIsOpenChangeWithAnalytics: OnIsOpenChange = useCallback(
    (change) => {
      onMenuOpenChange(change);
      onIsOpenChange(change);
    },
    [onMenuOpenChange, onIsOpenChange],
  );

  const onInputValueChange = useCallback(
    ({
      inputValue = '',
      type,
    }: {
      inputValue?: string;
      type?: DropdownStateChangeType;
    }) => {
      // Only user typing updates the analytics search text: selecting an item also
      // clears the input, and that reset lands before the select callback runs.
      if (type === useAutocomplete.stateChangeTypes.InputChange) {
        searchTextRef.current = inputValue;
      }
      setSearchText(inputValue);
      onInputValueChangeProp?.({ inputValue });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- leaving out searchTextRef as it is a ref
    [onInputValueChangeProp],
  );

  const getFilteredItems = useCallback(
    ({
      allItems,
      inputValue = '',
    }: {
      allItems: ItemWithGroup[];
      inputValue?: string;
    }) => {
      const search = inputValue.trim().toLowerCase();

      if (!search) return allItems;

      return allItems.filter((item) => {
        const { label, subtext, group } = item;
        const isSubtextSearchableFlag = isSubtextSearchable(group);
        return (
          (label && label.toString().toLowerCase().indexOf(search) >= 0) ||
          (subtext &&
            isSubtextSearchableFlag &&
            subtext.toString().toLowerCase().indexOf(search) >= 0)
        );
      });
    },
    [],
  );

  const renderMenuFunc = useCallback(
    (
      menuProps: ComponentProps<typeof DropdownMenu<ItemWithGroup>>,
      virtualProps: VirtualProps,
    ) => {
      renderMenuHandler(menuProps);

      const assigneePickerFooterProps: AssigneePickerFooterProps = {
        users: users.data,
        userOptionValueGetter: userOptionValue,
      };
      const { inputValue, ...restMenuProps } = menuProps;
      return renderMenu({
        menuProps: {
          isLoading,
          renderFooter,
          renderMenuItem: (menuItemProps) =>
            renderMenuItem({ ...menuItemProps, inputValue }),
          ...restMenuProps,
          setSelectedGroupKey: (groupKey) => {
            const group = getSetSelectedGroupKey(groupKey);
            setSelectedGroup(group);
            menuProps.setSelectedGroupKey?.(groupKey);
          },
        },
        virtualProps,
        assigneePickerFooterProps,
      });
    },
    [
      renderMenuHandler,
      users.data,
      userOptionValue,
      renderMenu,
      isLoading,
      getSetSelectedGroupKey,
      setSelectedGroup,
      renderFooter,
      renderMenuItem,
    ],
  );

  return {
    dropdownProps: {
      isLoading,
      groupedItems,
      getFilteredItems,
      renderMenu: renderMenuFunc,
      renderItems,
      showGroupItemCount: true,
      isAutocomplete: true,
      allowGroupFiltering: true,
      getItemKey: getAssigneeItemKey,
      itemToKey: getAssigneeItemKey,
      onItemSelect: onItemSelectWithAnalytics,
      onIsOpenChange: onIsOpenChangeWithAnalytics,
      onInputValueChange,
    },
  } satisfies ReturnType;
};
